import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Building2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  CreditCard,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Settings,
  Globe,
  Activity,
  Wallet,
  Percent,
  RefreshCw,
  Download,
  ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

// Format percentage
const formatPercent = (value) => {
  return `${(value * 100).toFixed(1)}%`;
};

export default function OperatorDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [config, setConfig] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);

  // Check if user is operator admin
  useEffect(() => {
    if (user && !user.is_operator_admin) {
      toast.error('Operator admin access required');
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        const [summaryRes, txRes, clubsRes, configRes] = await Promise.all([
          fetch(`${API}/api/accounting/operator/summary?period=${period}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API}/api/accounting/operator/transactions?limit=20`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API}/api/accounting/operator/clubs`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API}/api/accounting/config`)
        ]);

        if (summaryRes.ok) {
          const data = await summaryRes.json();
          setSummary(data);
          setConfig(data.config || {});
        }
        
        if (txRes.ok) {
          const data = await txRes.json();
          setTransactions(data.transactions || []);
        }
        
        if (clubsRes.ok) {
          const data = await clubsRes.json();
          setClubs(data.clubs || []);
        }
        
        if (configRes.ok) {
          const data = await configRes.json();
          setConfig(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Failed to fetch operator data:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, period]);

  // Save config
  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const response = await fetch(`${API}/api/accounting/operator/config?marketplace_commission_rate=${config.marketplace_commission_rate}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Configuration saved');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const operatorData = summary?.operator || {};
  const clubData = summary?.club_overview || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Building2 className="w-8 h-8 text-purple-400" />
              </div>
              <h1 className="text-3xl font-bold">Operator Dashboard</h1>
            </div>
            <p className="text-slate-400">Platform financial overview and management</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" className="border-slate-700 text-slate-300">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {/* Operator Revenue */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Operator Revenue</p>
                  <p className="text-2xl font-bold text-green-400">
                    {formatCurrency(operatorData.total_revenue)}
                  </p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">Commissions & fees</p>
            </CardContent>
          </Card>

          {/* Processing Costs */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Processing Costs</p>
                  <p className="text-2xl font-bold text-red-400">
                    {formatCurrency(operatorData.total_expenses)}
                  </p>
                </div>
                <div className="p-3 bg-red-500/20 rounded-full">
                  <CreditCard className="w-6 h-6 text-red-400" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">Stripe fees absorbed</p>
            </CardContent>
          </Card>

          {/* Net Profit */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Net Profit</p>
                  <p className={`text-2xl font-bold ${operatorData.net_balance >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                    {formatCurrency(operatorData.net_balance)}
                  </p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-full">
                  <Wallet className="w-6 h-6 text-purple-400" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">Revenue - costs</p>
            </CardContent>
          </Card>

          {/* Club Revenue */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Club Revenue</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {formatCurrency(clubData.total_revenue)}
                  </p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-full">
                  <Globe className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">Total club earnings</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-slate-800/50 border-slate-700">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="clubs">Clubs</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Revenue Breakdown */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-purple-400" />
                    Revenue Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(operatorData.breakdown || {}).map(([type, amount]) => (
                      amount > 0 && (
                        <div key={type} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              type.includes('commission') ? 'bg-green-500' :
                              type.includes('fee') ? 'bg-blue-500' : 'bg-purple-500'
                            }`} />
                            <span className="text-slate-300 capitalize">
                              {type.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <span className="font-medium">
                            {formatCurrency(amount)}
                          </span>
                        </div>
                      )
                    ))}
                    
                    {Object.keys(operatorData.breakdown || {}).length === 0 && (
                      <p className="text-slate-500 text-center py-4">No revenue data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-400" />
                    Platform Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-400">Commission Rate</span>
                      <Badge className="bg-purple-500/20 text-purple-400">
                        {formatPercent(config.marketplace_commission_rate || 0.10)}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-400">Stripe Fee Rate</span>
                      <Badge className="bg-blue-500/20 text-blue-400">
                        {formatPercent(config.stripe_fee_rate || 0.029)} + $0.30
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-400">Active Clubs</span>
                      <Badge className="bg-green-500/20 text-green-400">
                        {clubs.length}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-400">Total Platform Users</span>
                      <Badge className="bg-orange-500/20 text-orange-400">
                        {clubs.reduce((acc, c) => acc + c.total_users, 0)}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription className="text-slate-400">
                  Operator account activity
                </CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length > 0 ? (
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div 
                        key={tx.id}
                        className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${
                            tx.amount > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                          }`}>
                            {tx.amount > 0 ? (
                              <ArrowUpRight className="w-4 h-4 text-green-400" />
                            ) : (
                              <ArrowDownRight className="w-4 h-4 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{tx.description}</p>
                            <p className="text-sm text-slate-500 capitalize">
                              {tx.transaction_type?.replace(/_/g, ' ')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-medium ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(tx.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No transactions yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clubs Tab */}
          <TabsContent value="clubs">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle>Managed Clubs</CardTitle>
                <CardDescription className="text-slate-400">
                  All clubs on the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {clubs.map((club) => (
                    <div 
                      key={club.id}
                      className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                          <Globe className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-medium">{club.name}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {club.total_users} users
                            </span>
                            <span>•</span>
                            <span>{club.paid_members} full</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium text-green-400">
                            {formatCurrency(club.revenue)}
                          </p>
                          <p className="text-xs text-slate-500">Total Revenue</p>
                        </div>
                        <Badge variant={club.status === 'active' ? 'default' : 'secondary'}>
                          {club.status}
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Platform Configuration
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configure operator fees and commissions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Marketplace Commission Rate</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="0.5"
                        value={(config.marketplace_commission_rate || 0.10) * 100}
                        onChange={(e) => setConfig({
                          ...config,
                          marketplace_commission_rate: parseFloat(e.target.value) / 100
                        })}
                        className="bg-slate-700 border-slate-600"
                      />
                      <Percent className="w-5 h-5 text-slate-400" />
                    </div>
                    <p className="text-xs text-slate-500">
                      Percentage taken from each marketplace sale
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Stripe Processing Fee</Label>
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="font-medium">{formatPercent(config.stripe_fee_rate || 0.029)} + $0.30</p>
                      <p className="text-xs text-slate-500">Set by Stripe (not configurable)</p>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSaveConfig}
                  disabled={savingConfig}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {savingConfig ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="w-4 h-4 mr-2" />
                  )}
                  Save Configuration
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
